import { Navigate, Outlet, useLocation } from "react-router-dom";
import { useAtom } from "jotai";
import { authenticationAtom, jwtTokensAtom } from './State';
import { jwtAuthService } from './JWTAuthService';
import { userAPI } from './UserAPI';
import { loginPathWithTenant } from './utilities/loginRedirect';
import { useEffect, useState } from 'react';
import { OverlayBotProvider } from './components/overlay-bot/OverlayBotContext';
import OverlayBot from './components/overlay-bot/OverlayBot';
import { APP_SHELL_PATHS } from './components/AppShell';

export default function Protected() {
  const location = useLocation();
  const [authentication, setAuthentication] = useAtom(authenticationAtom);
  const [jwtTokens] = useAtom(jwtTokensAtom);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isChecking, setIsChecking] = useState(true);

  useEffect(() => {
    const checkAuth = async () => {
      // Check if we have a valid JWT token
      const accessToken = jwtAuthService.getAccessToken();
      const hasJWT = accessToken && !jwtAuthService.isTokenExpired(accessToken);
      
      // Check if we have valid session-based authentication
      // An empty object {} or null should not be considered authenticated
      // We need actual user data (customer, user, or email)
      const hasSession = authentication && 
        authentication !== null &&
        typeof authentication === 'object' && 
        Object.keys(authentication).length > 0 &&
        (authentication.customer || authentication.user || authentication.email);

      const authenticated = hasJWT || !!hasSession;
      setIsAuthenticated(authenticated);

      // Pre-fetch the customer object while still in the checking phase so
      // that every component inside <Outlet> already has it when they first render.
      //
      // Fetched on every page load, not only when missing: the atom lives in localStorage, so a
      // stored copy used to stay until logout, and a change to the customer (e.g. its
      // `configuration.portalPanels`, set in Mongo) never arrived. With a copy at hand the page
      // does not wait for the fetch; the fresh one replaces it when it lands.
      const customerId = (authentication as any)?.customer;
      if (authenticated && customerId) {
        const refresh = userAPI
          .getCustomer(customerId)
          .then((customerData: any) => {
            if (customerData) {
              setAuthentication((prev: any) => ({ ...prev, customerObject: customerData }));
            }
          })
          .catch((err: unknown) => {
            console.warn("[Protected] Could not pre-fetch customerObject:", err);
          });
        if (!(authentication as any)?.customerObject) {
          await refresh;
        }
      }

      setIsChecking(false);
    };

    checkAuth();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authentication?.customer, jwtTokens]);

  if (isChecking) {
    // An empty page in the page color: right after signing in this sits
    // between the fading login and the shell's entrance, and a "Loading..."
    // line would flash in between.
    return <div className="min-h-screen bg-background" aria-busy="true" data-testid="protected-checking" />;
  }

  if (!isAuthenticated) {
    // Carry the page that was asked for, so signing in lands there rather than
    // on the assistant — a deep link to a protected page has to survive login.
    return <Navigate to={loginPathWithTenant(`${location.pathname}${location.search}`)} replace />;
  }

  // Every signed-in page carries the assistant — except the chat page, where a
  // floating second chat over the real one would be absurd, and the AppShell
  // pages, where the "Fragen" side panel surfaces the same conversation.
  const onChatPage = location.pathname.startsWith('/chatbot');
  const onShellPage = APP_SHELL_PATHS.some((path) => location.pathname.startsWith(path));
  // The simulated gefma download portal stands on its own, without our assistant.
  const onStandalonePage = location.pathname.startsWith('/gefma-richtlinien');

  return (
    <OverlayBotProvider>
      <Outlet />
      {onChatPage || onShellPage || onStandalonePage ? null : <OverlayBot />}
    </OverlayBotProvider>
  );
};